import i18next from "i18next";
import { AxiosError } from "axios";
import { useContext } from "react";
import { useHistory } from "react-router";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";
import { FeedbackContext } from "context/Feedback";
import { FeedbackLocation } from "components/Feedback/FeedbackPopup";

import { getAIRiskApi, isAIRiskMockMode, isTimeoutError } from "../api";
import { AIAcceptRequest, AIAcceptResponse } from "../types";
import { AI_RISK_SESSION_QUERY_KEY } from "./sessionCache";

interface AcceptAIHazardVariables {
  hazardId: string;
  request: AIAcceptRequest;
  // Supplied by the Accept Gate for tracking (warnings were acknowledged).
  had_warnings?: boolean;
}

// POST /hazards/:hazardId/accept — the ONLY path by which AI output enters
// project.risks/RiskGroupping (single atomic server transaction; this flow
// never issues POST /projects/:id/risks or a full-project PUT itself).
// Conventions mirror useUpdateRisk: refetchQueries + ?saved=true redirect +
// displayFeedback.
export const useAcceptAIHazard = (projectId: string) => {
  const client = useClient();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { track } = useContext(TrackingContext);
  const { displayFeedback } = useContext(FeedbackContext);
  const { displayNotification } = useContext(ControlContext);

  return useMutation<AIAcceptResponse, Error, AcceptAIHazardVariables>(
    ({ hazardId, request }) =>
      getAIRiskApi(client).acceptHazard(projectId, hazardId, request),
    {
      onSuccess: (_response, { request, had_warnings }) => {
        queryClient.refetchQueries(["risks", projectId]);
        queryClient.refetchQueries(["projects", projectId]);
        queryClient.refetchQueries([AI_RISK_SESSION_QUERY_KEY, projectId]);

        track("ai risk: hazard accepted", {
          mitigations: request.risk.mitigations.length,
          had_warnings: !!had_warnings,
        });
        displayFeedback!(FeedbackLocation.Risk);

        const mockSuffix = isAIRiskMockMode()
          ? ` ${i18next.t("risks.ai.notifications.mockSuffix")}`
          : "";
        displayNotification!(
          i18next.t("risks.ai.notifications.accepted.title"),
          `${i18next.t("risks.ai.notifications.accepted.message")}${mockSuffix}`
        );

        history.push(`/projects/${projectId}/risks/ai?saved=true`);
      },
      onError: (error) => {
        if ((error as AxiosError).response?.status === 409) {
          // Already accepted (e.g. retry after an unknown outcome) — re-GET
          // the persisted session; idempotency per contract endpoint 5.
          queryClient.refetchQueries([AI_RISK_SESSION_QUERY_KEY, projectId]);
          displayNotification!(
            i18next.t("risks.ai.errors.alreadyAccepted"),
            error.message,
            "error"
          );

          return;
        }

        displayNotification!(
          i18next.t(
            isTimeoutError(error)
              ? "risks.ai.errors.timeout"
              : "risks.ai.errors.acceptFailed"
          ),
          error.message,
          "error"
        );
      },
    }
  );
};
